
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ExternalLink, Link } from "lucide-react";

interface VerificationInputProps {
  onVerify: (code: string) => void;
  isVerifying: boolean;
  error?: string;
  email?: string;
  onResend?: () => void;
}

export function VerificationInput({ 
  isVerifying, 
  error, 
  email, 
  onResend 
}: VerificationInputProps) {
  // For testing: Show verification notification
  useEffect(() => {
    if ((import.meta.env.DEV || window.location.hostname.includes('lovableproject')) && email) {
      // Listen for magic link sent events
      const handleShowMagicLinkSent = (e: CustomEvent) => {
        if (e.detail.email === email) {
          console.log(`Magic link sent to ${e.detail.email}`);
        }
      };

      window.addEventListener('show-magic-link-sent', handleShowMagicLinkSent as EventListener);

      return () => {
        window.removeEventListener('show-magic-link-sent', handleShowMagicLinkSent as EventListener);
      };
    }
  }, [email]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-center text-lg font-medium text-gray-700">
          Check your email
        </h3>
        <p className="text-center text-sm text-gray-600">
          We've sent a magic link to {email}. Please click the link in the email to continue.
        </p>
      </div>
      
      {/* For testing in sandbox: Show verification info */}
      {(import.meta.env.DEV || window.location.hostname.includes('lovableproject')) && (
        <div className="bg-blue-100 border border-blue-300 text-blue-800 px-4 py-2 rounded-md mb-4">
          <p className="text-center text-sm font-medium">
            Test Mode: In a real environment, a magic link would be sent to your email
          </p>
          <p className="text-center text-xs mt-1">
            For testing, you can proceed without clicking an actual link
          </p>
        </div>
      )}
      
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md">
          <div className="flex items-center mb-2">
            <AlertTriangle size={16} className="mr-2" />
            <p className="text-sm font-medium">There was a problem verifying your email</p>
          </div>
          <p className="text-xs">{error}</p>
          
          <div className="mt-2 text-xs">
            <p>Common solutions:</p>
            <ul className="list-disc pl-4 mt-1 space-y-1">
              <li>Check your spam folder for the magic link email</li>
              <li>Try using a different browser</li>
              <li>Close all browser tabs and start over</li>
              <li>Try using a non-private/incognito browser window</li>
              <li>If using iOS, use Safari instead of an embedded browser</li>
            </ul>
          </div>
        </div>
      )}

      <div className="flex justify-center items-center space-y-2">
        {isVerifying ? (
          <div className="animate-pulse">Verifying...</div>
        ) : (
          <div className="text-center space-y-2">
            <div className="text-sm text-gray-600">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="mt-2">Waiting for you to click the magic link in your email</p>
              <p className="mt-1 text-xs text-gray-500">
                After clicking the link, you might need to return to this tab
              </p>
            </div>
            
            <div className="mt-3 text-xs text-gray-600 border-t border-gray-200 pt-3">
              <p className="mb-2">Having trouble? Try these tips:</p>
              <ul className="list-disc pl-4 space-y-1 text-left">
                <li>After clicking the link, come back to this tab</li>
                <li>If on mobile, copy the link and paste it in this browser</li>
                <li>Check your spam folder</li>
              </ul>
            </div>
            
            {onResend && (
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onResend}
                  className="mb-2"
                >
                  Resend magic link
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
